/* Supply › Sales › Sales Return (Credit Note)
 * Accept goods back from a customer against an original sales invoice. The
 * operator looks up the sale by its invoice number, which prefills the sold
 * lines; posting returns stock (RETURN_IN) and credits the customer. A credit
 * note is the GST document. See GRN_NOTES_API_SPEC.md §3. */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { cbFmtINR, cbFmtNum } from "../supplyData";
import {
  Button,
  Pill,
  Input,
  Select,
  Table,
  Tr,
  Td,
  SectionHeader,
  ToolbarBar,
  DateRangeFilter,
  KpiBox,
  Eyebrow,
  LoadingState,
  ErrorState,
  IconPlus,
  IconX,
  IconCheck,
  IconEdit,
  IconChevRight,
  IconBook,
  KV,
} from "../supplyKit";
import notesService from "./notesService";
import supplyService from "../../../Services/supplyService";
import { isPharmacyHead } from "../../../Services/warehouseContext";

/* Must match the backend reason enum (there is no CUSTOMER_RETURN value). */
const REASONS = ["EXPIRY", "DAMAGE", "RATE_DIFF", "SHORT_SUPPLY", "WRONG_ITEM", "SCHEME", "OTHER"];

/* How the money goes back — must match the backend RefundMode enum.
   CUSTOMER_CREDIT is the only mode the system settles itself (it grants store
   credit); the payout modes are recorded only, the money moves outside the
   system; RECEIVABLE is for an on-account sale that was never paid.
   ORIGINAL_PAYMENT_MODE is request-only: resolved from the sale on posting, which
   also sends a sale paid from store credit back to store credit, so it is the
   default. */
const ORIGINAL = "ORIGINAL_PAYMENT_MODE";
const STORE_CREDIT = "CUSTOMER_CREDIT";
const REFUND_MODES = [
  ORIGINAL,
  "CASH",
  "UPI",
  "CARD",
  "NET_BANKING",
  "WALLET",
  "CHEQUE",
  STORE_CREDIT,
  "RECEIVABLE",
  "E_CLINIC_ONLINE",
  "B2C_ONLINE",
  "OTHER",
];
const refundModeLabel = (m) =>
  ({
    ORIGINAL_PAYMENT_MODE: "Original payment mode",
    CASH: "Cash",
    UPI: "UPI",
    CARD: "Card",
    NET_BANKING: "Net banking",
    WALLET: "Wallet",
    CHEQUE: "Cheque",
    CUSTOMER_CREDIT: "Store credit",
    RECEIVABLE: "On account (receivable)",
    E_CLINIC_ONLINE: "e-Clinic online",
    B2C_ONLINE: "B2C online",
    OTHER: "Other",
  }[m] || m);
/* Modes where a gateway / instrument reference is worth capturing. */
const TXN_MODES = new Set(["UPI", "CARD", "NET_BANKING", "WALLET", "CHEQUE", "E_CLINIC_ONLINE", "B2C_ONLINE", "OTHER"]);
const blankRefund = (mode = ORIGINAL) => ({ refundMode: mode, amount: "", transactionId: "" });

/* The server stores netAmount rounded to the whole rupee (HALF_UP) and demands
   the refunds add up to exactly that, so the plan is checked against the same
   figure. */
const roundRupee = (n) => Math.round(round2(n));

/* A refund plan as the API wants it. One line = "all of it this way", so its
   amount is omitted and the server takes the whole note; a split sends every
   amount. */
const toRefundsPayload = (plan) =>
  plan.map((r) => ({
    refundMode: r.refundMode,
    ...(plan.length > 1 ? { amount: round2(r.amount) } : {}),
    ...(TXN_MODES.has(r.refundMode) && String(r.transactionId || "").trim()
      ? { transactionId: String(r.transactionId).trim() }
      : {}),
  }));

/* Mirrors the backend's applyRefunds checks so the operator hears about a bad
   plan before saving. Returns an error string, or "" when the plan is valid.
   (The "sale paid from store credit must go back to store credit" rule needs the
   sale's tenders, which the ledger doesn't carry — the server enforces it.) */
const refundPlanError = (plan, net, { canStoreCredit }) => {
  if (!plan.length) return "Choose how the refund is paid.";
  if (plan.some((r) => r.refundMode === STORE_CREDIT) && !canStoreCredit)
    return "Store credit needs the sale to carry a patient or phone — refund another way.";
  if (plan.length === 1) return "";
  if (plan.some((r) => r.refundMode === ORIGINAL))
    return "Original payment mode can't be split — name the modes explicitly.";
  if (plan.filter((r) => r.refundMode === STORE_CREDIT).length > 1)
    return "Only one store-credit line is allowed.";
  if (plan.some((r) => !(Number(r.amount) > 0))) return "Enter an amount on every refund line.";
  const total = round2(plan.reduce((s, r) => s + (Number(r.amount) || 0), 0));
  if (total !== round2(net))
    return `Refund lines total ${cbFmtINR(total)} but the note is ${cbFmtINR(net)} (${cbFmtINR(round2(net - total))} ${
      net > total ? "left to allocate" : "over"
    }).`;
  return "";
};

/* A stored refund row (SalesReturnRefundResponse) back into an editable line. */
const fromStoredRefund = (r) => ({
  refundMode: r.refundMode || ORIGINAL,
  amount: r.amount != null ? String(r.amount) : "",
  transactionId: r.transactionId || "",
});
/* Quantities on a sales-return line are counted in whatever the sale was billed
   in — full strips or loose units. Label them so "5" is never ambiguous. */
const uomWord = (uom, n) => {
  const unit = String(uom || "STRIP").toUpperCase() === "UNIT" ? "Unit" : "Strip";
  return `${unit}${Number(n) === 1 ? "" : "s"}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/* ============================ Refund plan ============================ */
/* How the note's money goes back: one line by default (the whole note, one
   mode), or split across modes with an amount on each. */
const RefundPlanEditor = ({ plan, onChange, net, canStoreCredit, error }) => {
  const split = plan.length > 1;
  const setRow = (idx, patch) => onChange(plan.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const allocated = round2(plan.reduce((s, r) => s + (Number(r.amount) || 0), 0));
  const addSplit = () => {
    /* Splitting starts from the current single line: it keeps the mode (unless
       it was "original", which can't be split) and the whole note, and the new
       line starts empty for the operator to carve out. */
    const first = plan[0] || blankRefund("CASH");
    const firstMode = first.refundMode === ORIGINAL ? "CASH" : first.refundMode;
    if (!split) {
      onChange([
        { ...first, refundMode: firstMode, amount: String(net) },
        blankRefund(firstMode === STORE_CREDIT ? "CASH" : canStoreCredit ? STORE_CREDIT : "UPI"),
      ]);
    } else {
      onChange([...plan, { ...blankRefund("CASH"), amount: allocated < net ? String(round2(net - allocated)) : "" }]);
    }
  };
  const removeRow = (idx) => {
    const next = plan.filter((_, i) => i !== idx);
    /* Back to one line: it is "the whole note" again, amount no longer applies. */
    onChange(next.length === 1 ? [{ ...next[0], amount: "" }] : next);
  };
  const modeDisabled = (m, idx) =>
    (m === STORE_CREDIT && !canStoreCredit) ||
    (split && m === ORIGINAL) ||
    (m === STORE_CREDIT && plan.some((r, i) => i !== idx && r.refundMode === STORE_CREDIT));
  const cellInput = {
    height: 28,
    border: "1px solid var(--border-default)",
    borderRadius: 4,
    fontFamily: "var(--font-mono)",
    fontSize: 12,
    padding: "0 6px",
    background: "var(--bg-panel)",
  };

  return (
    <div
      style={{
        border: `1px solid ${error ? "var(--red-500)" : "var(--border-default)"}`,
        borderRadius: 6,
        padding: "10px 12px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        background: "var(--bg-panel)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Eyebrow>Refund · {cbFmtINR(net)}</Eyebrow>
        {split && (
          <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
            allocated {cbFmtINR(allocated)}
          </span>
        )}
        <Button size="sm" style={{ marginLeft: "auto" }} icon={<IconPlus size={12} />} onClick={addSplit}>
          {split ? "Add line" : "Split refund"}
        </Button>
      </div>

      {plan.map((r, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Select value={r.refundMode} onChange={(e) => setRow(i, { refundMode: e.target.value })} style={{ width: 210, maxWidth: "100%" }}>
            {REFUND_MODES.map((m) => (
              <option key={m} value={m} disabled={modeDisabled(m, i)}>
                {refundModeLabel(m)}
              </option>
            ))}
          </Select>
          {split ? (
            <input
              type="number"
              min={0}
              step="0.01"
              value={r.amount}
              onChange={(e) => setRow(i, { amount: e.target.value })}
              placeholder="Amount"
              style={{ ...cellInput, width: 110, textAlign: "right" }}
            />
          ) : (
            <span style={{ fontSize: 12, fontFamily: "var(--font-mono)", fontWeight: 600, minWidth: 110, textAlign: "right" }}>
              {cbFmtINR(net)}
            </span>
          )}
          {TXN_MODES.has(r.refundMode) && (
            <input
              value={r.transactionId}
              maxLength={100}
              onChange={(e) => setRow(i, { transactionId: e.target.value })}
              placeholder="Txn / ref no (optional)"
              style={{ ...cellInput, width: 200, maxWidth: "100%" }}
            />
          )}
          {split && (
            <Button size="sm" icon={<IconX size={12} />} onClick={() => removeRow(i)} />
          )}
        </div>
      ))}

      <div style={{ fontSize: 11, color: "var(--fg-muted)", lineHeight: 1.5 }}>
        {plan.some((r) => r.refundMode === ORIGINAL)
          ? "Paid back the way the sale was paid — resolved when the return is posted (an on-account sale becomes receivable)."
          : plan.some((r) => r.refundMode === STORE_CREDIT)
          ? "Store credit is added to the customer's balance on posting; other modes are recorded only — pay them out at the counter."
          : "Recorded only — the payout itself happens outside the system."}
        {!canStoreCredit && " Store credit is unavailable: the sale has no patient or phone."}
      </div>

      {error && <div style={{ fontSize: 12, color: "var(--red-700)", fontWeight: 600 }}>{error}</div>}
    </div>
  );
};

/* Read-only refund rows for a saved note. */
const RefundSummary = ({ note }) => {
  const refunds = note.refunds || [];
  if (!refunds.length) {
    return (
      <div style={{ fontSize: 12, color: "var(--amber-700)" }}>
        No refund recorded{note.status === "DRAFT" ? " — edit the draft to add one before posting." : "."}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
      {refunds.map((r, i) => (
        <div key={r.refundId ?? i} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
          <span>
            {refundModeLabel(r.refundMode)}
            {r.transactionId && (
              <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                {r.transactionId}
              </span>
            )}
          </span>
          <span style={{ fontFamily: "var(--font-mono)" }}>{cbFmtINR(r.amount ?? note.netAmount)}</span>
        </div>
      ))}
    </div>
  );
};

/* ============================ List ============================ */
const SalesReturn = () => {
  const [creating, setCreating] = useState(false);
  /* A pharmacy head sees every warehouse's notes, so the list must say which
     warehouse each belongs to and let them narrow to one. A regular user only
     ever sees their own, where the column and filter would be dead weight. */
  const isAdmin = isPharmacyHead();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  /* No warehouse filter: the list is scoped to the store selected app-wide, and
     notesService sends that automatically. */
  const [filters, setFilters] = useState({ q: "", status: "", fromDate: "", toDate: "" });
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const [pageState, setPageState] = useState({ page: 0, size: 20, totalPages: 0, totalElements: 0 });
  const setPageField = (k, v) => setPageState((s) => ({ ...s, [k]: v, ...(k === "size" ? { page: 0 } : {}) }));
  const [selected, setSelected] = useState(null); // noteId shown in the detail panel
  const [editId, setEditId] = useState(null); // noteId open in the edit drawer

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    const params = { page: pageState.page, size: pageState.size };
    if (filters.q) params.search = filters.q.trim();
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = filters.fromDate;
    if (filters.toDate) params.toDate = filters.toDate;
    notesService.salesReturns
      .list(params)
      .then((res) => {
        if (!alive) return;
        setRows(res.content || []);
        setPageState((s) => ({ ...s, totalPages: res.totalPages || 0, totalElements: res.totalElements || 0 }));
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        const msg = e?.message || "Could not load sales returns.";
        setError(msg);
        toast.error(msg);
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filters, pageState.page, pageState.size, reloadKey]);

  useEffect(() => {
    setPageState((s) => (s.page === 0 ? s : { ...s, page: 0 }));
  }, [filters]);

  if (creating) {
    return (
      <SalesReturnDrawer
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  if (editId != null) {
    return (
      <SalesReturnDetailDrawer
        id={editId}
        editable
        onClose={() => setEditId(null)}
        onSaved={() => {
          setEditId(null);
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  const start = pageState.totalElements === 0 ? 0 : pageState.page * pageState.size + 1;
  const end = Math.min(pageState.totalElements, (pageState.page + 1) * pageState.size);
  const selectedNote = rows.find((n) => n.noteId === selected) || null;

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="Sales Returns"
        sub="Accept goods back from a customer against a sales invoice (raises a credit note)"
        actions={
          <Button variant="primary" icon={<IconPlus size={14} />} onClick={() => setCreating(true)}>
            New Sales Return
          </Button>
        }
      />

      <ToolbarBar>
        <Input
          value={filters.q}
          onChange={(e) => setF("q", e.target.value)}
          placeholder="Note no / invoice / customer…"
          style={{ width: 260, maxWidth: "100%" }}
        />
        <Select value={filters.status} onChange={(e) => setF("status", e.target.value)}>
          <option value="">All statuses</option>
          {["DRAFT", "POSTED", "CANCELLED"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
        <DateRangeFilter
          from={filters.fromDate}
          to={filters.toDate}
          onFromChange={(v) => setF("fromDate", v)}
          onToChange={(v) => setF("toDate", v)}
        />
        <div style={{ marginLeft: "auto", fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
          {cbFmtNum(pageState.totalElements)} notes
        </div>
      </ToolbarBar>

      {loading ? (
        <LoadingState label="Loading sales returns…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => setReloadKey((k) => k + 1)} />
      ) : (
        <div
          className={`grid grid-cols-1 ${selectedNote ? "lg:grid-cols-[1fr_440px]" : ""}`}
          style={{
            display: "grid",
            gap: 12,
            alignItems: "start",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
            <Table
              columns={[
                { label: "Note #" },
                { label: "Date" },
                ...(isAdmin ? [{ label: "Warehouse" }] : []),
                { label: "Customer" },
                { label: "Against invoice" },
                { label: "Reason" },
                { label: "Items", align: "right" },
                { label: "Net", align: "right" },
                { label: "Status" },
                { label: "", width: 24 },
              ]}
            >
              {rows.length === 0 && (
                <tr>
                  <Td colSpan={isAdmin ? 10 : 9} style={{ textAlign: "center", color: "var(--fg-muted)", height: 80 }}>
                    No sales returns yet.
                  </Td>
                </tr>
              )}
              {rows.map((n) => (
                <Tr
                  key={n.noteId}
                  selected={selected === n.noteId}
                  onClick={() => setSelected(selected === n.noteId ? null : n.noteId)}
                >
                  <Td mono style={{ fontWeight: 600 }}>
                    {n.noteNumber || `#${n.noteId}`}
                  </Td>
                  <Td mono>{String(n.noteDate).slice(0, 10)}</Td>
                  {isAdmin && <Td>{n.warehouseName || n.warehouseId || "—"}</Td>}
                  <Td>{n.customerName || n.supplierName || "—"}</Td>
                  <Td mono style={{ color: "var(--fg-muted)" }}>
                    {n.linkedDocNumber}
                  </Td>
                  <Td>
                    <Pill variant="neutral">{n.reason || "—"}</Pill>
                  </Td>
                  <Td mono right>
                    {cbFmtNum(n.itemCount)}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(n.netAmount)}
                  </Td>
                  <Td>
                    <Pill variant={n.status === "POSTED" ? "green" : n.status === "CANCELLED" ? "red" : "neutral"} dot>
                      {n.status}
                    </Pill>
                  </Td>
                  <Td>
                    <IconChevRight size={12} style={{ color: "var(--fg-muted)" }} />
                  </Td>
                </Tr>
              ))}
            </Table>

            <ToolbarBar>
              <Select value={pageState.size} onChange={(e) => setPageField("size", Number(e.target.value))}>
                {[20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </Select>
              <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                  {start}–{end} of {cbFmtNum(pageState.totalElements)}
                </span>
                <Button size="sm" disabled={pageState.page <= 0} onClick={() => setPageField("page", Math.max(0, pageState.page - 1))}>
                  ← Prev
                </Button>
                <Button
                  size="sm"
                  disabled={pageState.page + 1 >= pageState.totalPages}
                  onClick={() => setPageField("page", pageState.page + 1)}
                >
                  Next →
                </Button>
              </div>
            </ToolbarBar>
          </div>

          {selectedNote && (
            <SalesReturnDetailPanel
              id={selectedNote.noteId}
              fallback={selectedNote}
              onClose={() => setSelected(null)}
              onEdit={(id) => setEditId(id)}
              onChanged={() => {
                setReloadKey((k) => k + 1);
                setSelected(null);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

/* ============================ Create ============================ */
const SalesReturnDrawer = ({ onClose, onSaved }) => {
  const [invoiceNo, setInvoiceNo] = useState("");
  const [looking, setLooking] = useState(false);
  const [sale, setSale] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("EXPIRY");
  const [noteDate, setNoteDate] = useState(today());
  const [remarks, setRemarks] = useState("");
  const [refundPlan, setRefundPlan] = useState([blankRefund()]);
  const [saving, setSaving] = useState(false);

  const fetchInvoice = async () => {
    if (looking) return;
    setLooking(true);
    setSale(null);
    setLines([]);
    setRefundPlan([blankRefund()]);
    try {
      const { sale: s, lines: ls } = await notesService.lookupSaleByInvoice(invoiceNo);
      setSale(s);
      setLines(ls);
      if (!ls.length) toast.warn("Invoice found, but it has no line items to return.");
      else if (s.fullyReturned || ls.every((l) => (l.eligibleQty || 0) <= 0))
        toast.warn("This invoice is already fully returned — nothing left to return.");
    } catch (e) {
      toast.error(e?.message || "Could not fetch the invoice.");
    } finally {
      setLooking(false);
    }
  };

  const setQty = (idx, val) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, returnQty: val } : l)));

  const computed = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      count = 0;
    lines.forEach((l) => {
      const c = notesService.calcLine(l);
      if (c.qty > 0) {
        taxable += c.taxableAmount;
        cgst += c.cgstAmount;
        sgst += c.sgstAmount;
        count += 1;
      }
    });
    return { taxable, cgst, sgst, net: roundRupee(taxable + cgst + sgst), count };
  }, [lines]);

  const canStoreCredit = !!(sale && (sale.patientId || sale.customerPhone));
  const refundError = computed.count > 0 ? refundPlanError(refundPlan, computed.net, { canStoreCredit }) : "";

  /* Only the still-eligible quantity may be returned (soldQty − already returned). */
  const overReturn = lines.some((l) => (Number(l.returnQty) || 0) > (l.eligibleQty ?? l.soldQty));
  const canSave = sale && computed.count > 0 && !overReturn && !refundError && !saving;

  /* SalesReturnRequest: per-item the server only needs the lot + raw amounts
     (inventoryId, quantity, unitOfMeasure, sellingPrice, discountAmount,
     gstPercentage) and computes the tax breakdown; header carries
     salesInvoiceId + netAmount. */
  const buildPayload = (status) => {
    const items = lines
      .filter((l) => (Number(l.returnQty) || 0) > 0)
      .map((l) => {
        const c = notesService.calcLine(l);
        return {
          inventoryId: l.inventoryId,
          quantity: c.qty,
          /* Required by the API; whether `quantity` counts strips or loose
             units. Defaults to STRIP server-side, so a unit line MUST say so or
             it is refunded as whole strips. */
          unitOfMeasure: String(l.uom || "STRIP").toUpperCase(),
          /* Per STRIP — the API divides by unitsPerStrip for a UNIT line.
             `l.rate` is the per-billed-unit figure the UI does its math in. */
          sellingPrice: Number(l.sellingPriceStrip ?? l.rate) || 0,
          discountAmount: c.discountAmount,
          gstPercentage: c.gstPercentage,
        };
      });
    return {
      returnDate: noteDate,
      salesInvoiceId: sale.id,
      warehouseId: sale.warehouseId,
      reason,
      remarks: remarks || "",
      status,
      /* Whole rupees, as the server stores it — the refunds must add up to it. */
      netAmount: computed.net,
      items,
      refunds: toRefundsPayload(refundPlan),
    };
  };

  const save = async (post) => {
    if (!canSave) return;
    setSaving(true);
    try {
      const created = await notesService.salesReturns.create(buildPayload("DRAFT"));
      const id = created?.returnId ?? created?.noteId ?? created?.id;
      if (post && id != null) await notesService.salesReturns.post(id);
      toast.success(post ? "Sales return posted." : "Sales return saved as draft.");
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Could not save the sales return.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title="New Sales Return"
        sub="Fetch the original sale by invoice number, then enter return quantities"
        actions={
          <Button icon={<IconX size={14} />} onClick={onClose}>
            Cancel
          </Button>
        }
      />

      <ToolbarBar>
        <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Sales invoice no</span>
        <Input
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchInvoice()}
          placeholder="invoice no or order id"
          style={{ width: 220, maxWidth: "100%" }}
        />
        <Button variant="primary" onClick={fetchInvoice} disabled={looking || !invoiceNo.trim()}>
          {looking ? "Fetching…" : "Fetch"}
        </Button>
        {sale && (
          <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            {sale.customerName || "—"} · {sale.warehouseName || sale.warehouseId} · billed {String(sale.invoiceDate).slice(0, 10)}
          </span>
        )}
      </ToolbarBar>

      {!sale ? (
        looking ? (
          <LoadingState label="Fetching sale…" />
        ) : (
          <div
            style={{
              padding: "28px 16px",
              textAlign: "center",
              color: "var(--fg-muted)",
              fontSize: 13,
              border: "1px dashed var(--border-default)",
              borderRadius: 6,
            }}
          >
            Enter the customer's sales invoice number (or order id) and click <strong>Fetch</strong> to load returnable lines.
          </div>
        )
      ) : (
        <>
          <ToolbarBar>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Reason</span>
            <Select value={reason} onChange={(e) => setReason(e.target.value)}>
              {REASONS.map((r) => (
                <option key={r} value={r}>
                  {r.replace(/_/g, " ")}
                </option>
              ))}
            </Select>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Note date</span>
            <Input type="date" value={noteDate} onChange={(e) => setNoteDate(e.target.value)} style={{ width: 150, maxWidth: "100%" }} />
            <Input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Remarks (optional)"
              style={{ width: 240, maxWidth: "100%" }}
            />
          </ToolbarBar>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
            <KpiBox label="Lines to return" value={cbFmtNum(computed.count)} variant="ink" />
            <KpiBox label="Taxable" value={cbFmtINR(computed.taxable)} variant="ink" />
            <KpiBox label="GST (CGST+SGST)" value={cbFmtINR(computed.cgst + computed.sgst)} variant="ink" />
            <KpiBox label="Refund value" value={cbFmtINR(computed.net)} variant="green" />
          </div>

          <Eyebrow style={{ display: "block" }}>Sold lines · enter quantity to return (≤ eligible)</Eyebrow>
          <Table
            columns={[
              { label: "Medicine" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "Sold", align: "right" },
              { label: "Returned", align: "right" },
              { label: "Eligible", align: "right" },
              { label: "Rate", align: "right" },
              { label: "GST %", align: "right" },
              { label: "Return qty", align: "right" },
              { label: "Refund", align: "right" },
            ]}
          >
            {lines.map((l, i) => {
              const c = notesService.calcLine(l);
              const over = (Number(l.returnQty) || 0) > l.eligibleQty;
              const noneEligible = l.eligibleQty <= 0;
              return (
                <Tr key={`${l.medicineCode}-${l.batchNo}-${i}`}>
                  <Td>
                    <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName || l.medicineCode}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                      {l.medicineCode}
                    </span>
                    {l.fullyReturned && (
                      <span style={{ marginLeft: 6, fontSize: 10, color: "var(--green-700)", fontWeight: 600 }}>
                        fully returned
                      </span>
                    )}
                  </Td>
                  <Td mono>{l.batchNo}</Td>
                  <Td mono>{l.expiryDate}</Td>
                  <Td mono right>
                    {cbFmtNum(l.soldQty)} <span style={{ color: "var(--fg-muted)" }}>{uomWord(l.uom, l.soldQty)}</span>
                  </Td>
                  <Td mono right style={{ color: "var(--fg-muted)" }}>
                    {cbFmtNum(l.returnedQty)} <span style={{ color: "var(--fg-muted)" }}>{uomWord(l.uom, l.returnedQty)}</span>
                  </Td>
                  <Td mono right style={{ fontWeight: 600, color: noneEligible ? "var(--fg-muted)" : "var(--fg-strong)" }}>
                    {cbFmtNum(l.eligibleQty)} <span style={{ fontWeight: 400 }}>{uomWord(l.uom, l.eligibleQty)}</span>
                  </Td>
                  <Td mono right>
                    {cbFmtINR(l.rate)}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(l.gstPercentage)}%
                  </Td>
                  <Td right>
                    <input
                      type="number"
                      min={0}
                      max={l.eligibleQty}
                      value={l.returnQty}
                      disabled={noneEligible}
                      onChange={(e) => setQty(i, e.target.value)}
                      title={noneEligible ? "Nothing left to return on this line" : `Up to ${l.eligibleQty} ${uomWord(l.uom, l.eligibleQty).toLowerCase()} returnable`}
                      style={{
                        width: 80,
                        height: 26,
                        textAlign: "right",
                        border: `1px solid ${over ? "var(--red-500)" : "var(--border-default)"}`,
                        borderRadius: 4,
                        fontFamily: "var(--font-mono)",
                        fontSize: 12,
                        padding: "0 6px",
                        background: noneEligible ? "var(--bg-subtle)" : "var(--bg-panel)",
                        opacity: noneEligible ? 0.6 : 1,
                      }}
                    />
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(c.lineTotal)}
                  </Td>
                </Tr>
              );
            })}
          </Table>

          {overReturn && (
            <div style={{ fontSize: 12, color: "var(--red-700)", fontWeight: 600 }}>
              One or more return quantities exceed the eligible (still-returnable) quantity.
            </div>
          )}

          {computed.count > 0 && (
            <RefundPlanEditor
              plan={refundPlan}
              onChange={setRefundPlan}
              net={computed.net}
              canStoreCredit={canStoreCredit}
              error={refundError}
            />
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => save(false)} disabled={!canSave}>
              Save draft
            </Button>
            <Button variant="primary" onClick={() => save(true)} disabled={!canSave}>
              {saving ? "Posting…" : "Post return"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

/* ====================== View / Edit a single note ====================== */
/* Loads a sales return (GET /{id}); editable for DRAFT (qty per line + reason +
   remarks) -> PATCH /{id}, with optional post. */
const SalesReturnDetailDrawer = ({ id, editable, onClose, onSaved }) => {
  const [note, setNote] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState("EXPIRY");
  const [remarks, setRemarks] = useState("");
  const [refundPlan, setRefundPlan] = useState([blankRefund()]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    notesService.salesReturns
      .get(id)
      .then((n) => {
        if (!alive) return;
        setNote(n);
        setReason(n.reason || "EXPIRY");
        setRemarks(n.remarks || "");
        /* A single stored line is "the whole note", so its amount is dropped and
           re-derived from the (possibly edited) net. A draft saved before refunds
           existed has none — start it on the default. */
        const stored = (n.refunds || []).map(fromStoredRefund);
        setRefundPlan(
          stored.length === 0 ? [blankRefund()] : stored.length === 1 ? [{ ...stored[0], amount: "" }] : stored
        );
        setLines(
          (n.items || []).map((it) => {
            const qty = Number(it.quantity) || 0;
            /* `sellingPrice` is per STRIP; `quantity` counts loose units on a
               UNIT line, so bring the rate down before deriving anything from
               it — otherwise gross (and the discount % backed out of it) is
               unitsPerStrip times too big. */
            const uom = String(it.unitOfMeasure || "STRIP").toUpperCase();
            const unitsPerStrip = Number(it.unitsPerStrip) || null;
            const perBilled = uom === "UNIT" && unitsPerStrip > 0 ? unitsPerStrip : 1;
            const sellingPriceStrip = Number(it.sellingPrice) || 0;
            const rate = sellingPriceStrip / perBilled;
            const gross = qty * rate;
            return {
              inventoryId: it.inventoryId,
              medicineCode: it.medicineCode,
              medicineName: it.medicineName,
              batchNo: it.batchNo,
              expiryDate: it.expiryDate,
              uom,
              unitsPerStrip,
              sellingPriceStrip,
              rate,
              gstPercentage: Number(it.gstPercentage) || 0,
              discountPercentage: gross > 0 ? round2(((Number(it.discountAmount) || 0) / gross) * 100) : 0,
              returnQty: qty,
            };
          })
        );
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the sales return.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const setQty = (idx, val) =>
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, returnQty: val } : l)));

  const computed = useMemo(() => {
    let taxable = 0,
      cgst = 0,
      sgst = 0,
      count = 0;
    lines.forEach((l) => {
      const c = notesService.calcLine(l);
      if (c.qty > 0) {
        taxable += c.taxableAmount;
        cgst += c.cgstAmount;
        sgst += c.sgstAmount;
        count += 1;
      }
    });
    return { taxable, cgst, sgst, net: roundRupee(taxable + cgst + sgst), count };
  }, [lines]);

  const canStoreCredit = !!(note && (note.patientId || note.customerPhone));
  const refundError =
    editable && computed.count > 0 ? refundPlanError(refundPlan, computed.net, { canStoreCredit }) : "";
  const canSave = editable && note && computed.count > 0 && !refundError && !saving;

  const buildPayload = (status) => ({
    returnDate: String(note.noteDate || today()).slice(0, 10),
    salesInvoiceId: note.linkedDocId,
    warehouseId: note.warehouseId,
    reason,
    remarks: remarks || "",
    status,
    netAmount: computed.net,
    /* PATCH replaces the refund plan wholesale — always send it. */
    refunds: toRefundsPayload(refundPlan),
    items: lines
      .filter((l) => (Number(l.returnQty) || 0) > 0)
      .map((l) => {
        const c = notesService.calcLine(l);
        return {
          inventoryId: l.inventoryId,
          quantity: c.qty,
          /* Required by the API and defaulted to STRIP server-side. */
          unitOfMeasure: String(l.uom || "STRIP").toUpperCase(),
          /* Per STRIP — the API divides by unitsPerStrip for a UNIT line. */
          sellingPrice: Number(l.sellingPriceStrip ?? l.rate) || 0,
          discountAmount: c.discountAmount,
          gstPercentage: c.gstPercentage,
        };
      }),
  });

  const save = async (post) => {
    if (!canSave) return;
    setSaving(true);
    try {
      await notesService.salesReturns.update(id, buildPayload("DRAFT"));
      if (post) await notesService.salesReturns.post(id);
      toast.success(post ? "Sales return posted." : "Draft updated.");
      onSaved();
    } catch (e) {
      toast.error(e?.message || "Could not save the sales return.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cb-fade-in" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader
        title={editable ? "Edit Sales Return" : "Sales Return"}
        sub={note ? `${note.noteNumber || `#${note.noteId}`} · against ${note.linkedDocNumber || "—"}` : "Loading…"}
        actions={
          <Button icon={<IconX size={14} />} onClick={onClose}>
            Close
          </Button>
        }
      />

      {loading ? (
        <LoadingState label="Loading sales return…" />
      ) : error ? (
        <ErrorState message={error} onRetry={() => onClose()} />
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ display: "grid", gap: 10 }}>
            <KpiBox label="Customer" value={note.customerName || "—"} variant="ink" />
            <KpiBox label="Warehouse" value={note.warehouseName || note.warehouseId || "—"} variant="ink" />
            <KpiBox label="Status" value={note.status} variant={note.status === "POSTED" ? "green" : note.status === "CANCELLED" ? "red" : "ink"} />
            <KpiBox label="Refund value" value={cbFmtINR(editable ? computed.net : note.netAmount)} variant="green" />
          </div>

          <ToolbarBar>
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Reason</span>
            {editable ? (
              <Select value={reason} onChange={(e) => setReason(e.target.value)}>
                {REASONS.map((r) => (
                  <option key={r} value={r}>
                    {r.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
            ) : (
              <Pill variant="neutral">{note.reason || "—"}</Pill>
            )}
            <span style={{ fontSize: 11, color: "var(--fg-muted)", fontWeight: 600 }}>Date</span>
            <span style={{ fontSize: 12, fontFamily: "var(--font-mono)" }}>{String(note.noteDate).slice(0, 10)}</span>
            {editable ? (
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks (optional)" style={{ width: 240, maxWidth: "100%" }} />
            ) : (
              note.remarks && <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>{note.remarks}</span>
            )}
          </ToolbarBar>

          <Table
            columns={[
              { label: "Medicine" },
              { label: "Batch" },
              { label: "Expiry" },
              { label: "Rate", align: "right" },
              { label: "GST %", align: "right" },
              { label: "Qty", align: "right" },
              { label: "Refund", align: "right" },
            ]}
          >
            {lines.map((l, i) => {
              const c = notesService.calcLine(l);
              return (
                <Tr key={`${l.medicineCode}-${l.batchNo}-${i}`}>
                  <Td>
                    <span style={{ fontWeight: 600, color: "var(--fg-strong)" }}>{l.medicineName || l.medicineCode}</span>
                    <span style={{ marginLeft: 6, fontSize: 11, color: "var(--fg-muted)", fontFamily: "var(--font-mono)" }}>
                      {l.medicineCode}
                    </span>
                  </Td>
                  <Td mono>{l.batchNo}</Td>
                  <Td mono>{l.expiryDate}</Td>
                  <Td mono right>
                    {cbFmtINR(l.rate)}
                  </Td>
                  <Td mono right>
                    {cbFmtNum(l.gstPercentage)}%
                  </Td>
                  <Td right>
                    {editable ? (
                      <input
                        type="number"
                        min={0}
                        value={l.returnQty}
                        onChange={(e) => setQty(i, e.target.value)}
                        style={{
                          width: 80,
                          height: 26,
                          textAlign: "right",
                          border: "1px solid var(--border-default)",
                          borderRadius: 4,
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          padding: "0 6px",
                        }}
                      />
                    ) : (
                      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{cbFmtNum(l.returnQty)}</span>
                    )}
                  </Td>
                  <Td mono right style={{ fontWeight: 600 }}>
                    {cbFmtINR(c.lineTotal)}
                  </Td>
                </Tr>
              );
            })}
          </Table>

          {editable ? (
            computed.count > 0 && (
              <RefundPlanEditor
                plan={refundPlan}
                onChange={setRefundPlan}
                net={computed.net}
                canStoreCredit={canStoreCredit}
                error={refundError}
              />
            )
          ) : (
            <div style={{ border: "1px solid var(--border-default)", borderRadius: 6, padding: "10px 12px" }}>
              <Eyebrow style={{ display: "block", marginBottom: 6 }}>Refund</Eyebrow>
              <RefundSummary note={note} />
            </div>
          )}

          {editable && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={() => save(false)} disabled={!canSave}>
                {saving ? "Saving…" : "Save draft"}
              </Button>
              <Button variant="primary" onClick={() => save(true)} disabled={!canSave}>
                {saving ? "Posting…" : "Post return"}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ================= Detail panel (GRN-style master/detail) ================= */
const panelTh = {
  padding: "5px 8px",
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "var(--fg-muted)",
  textAlign: "left",
  borderBottom: "1px solid var(--border-default)",
  whiteSpace: "nowrap",
};
const panelTd = { padding: "6px 8px", borderBottom: "1px solid var(--border-subtle)", verticalAlign: "middle" };
const statusVariant = (s) => (s === "POSTED" ? "green" : s === "CANCELLED" ? "red" : "neutral");

const SalesReturnDetailPanel = ({ id, fallback, onClose, onEdit, onChanged }) => {
  const navigate = useNavigate();
  const [note, setNote] = useState(fallback || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    notesService.salesReturns
      .get(id)
      .then((n) => {
        if (!alive) return;
        setNote(n);
        setLoading(false);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.message || "Could not load the sales return.");
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [id]);

  const runWith = async (label, fn) => {
    setBusy(true);
    try {
      await fn();
      toast.success(label);
      onChanged();
    } catch (e) {
      toast.error(e?.message || "Action failed.");
    } finally {
      setBusy(false);
    }
  };
  const post = () => {
    if (!window.confirm("Post this sales return? Stock will be restocked (RETURN_IN) and the refund recorded as planned.")) return;
    runWith("Sales return posted.", () => notesService.salesReturns.post(id));
  };
  const cancel = () => {
    const reason = window.prompt("Reason for cancelling this sales return?");
    if (reason == null) return;
    if (!reason.trim()) {
      toast.error("A cancellation reason is required.");
      return;
    }
    runWith("Sales return cancelled.", () => notesService.salesReturns.cancel(id, reason.trim()));
  };
  const viewLedger = () => navigate("/inventory", { state: { tab: "movements", refId: id } });

  const n = note || fallback || {};
  const items = Array.isArray(n.items) ? n.items : [];
  const status = n.status || "DRAFT";
  const between = { display: "flex", justifyContent: "space-between" };
  const mono = { fontFamily: "var(--font-mono)" };

  return (
    <aside
      className="lg:sticky lg:top-3 lg:max-h-[calc(100vh-120px)]"
      style={{
        border: "1px solid var(--border-default)",
        borderRadius: 6,
        background: "var(--bg-panel)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid var(--border-default)",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 13, fontWeight: 700, color: "var(--fg-strong)" }}>
              {n.noteNumber || `#${n.noteId}`}
            </span>
            <Pill variant={statusVariant(status)} dot>
              {status}
            </Pill>
          </div>
          <div style={{ fontSize: 11, color: "var(--fg-muted)", marginTop: 4, lineHeight: 1.4 }}>
            {String(n.noteDate).slice(0, 10)} · {n.customerName || "—"} · invoice{" "}
            <span style={{ fontFamily: "var(--font-mono)" }}>{n.linkedDocNumber}</span>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            width: 24,
            height: 24,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            color: "var(--fg-muted)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconX size={14} />
        </button>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "12px 16px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error ? (
          <ErrorState message={error} onRetry={onClose} />
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <KV label="Customer" value={n.customerName || "—"} />
              <KV label="Warehouse" value={n.warehouseName || n.warehouseId || "—"} mono small />
              <KV label="Reason" value={(n.reason || "—").replace(/_/g, " ")} />
              <KV label="Remarks" value={n.remarks || "—"} />
            </div>

            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Eyebrow>Refund</Eyebrow>
                {!loading && n.refundSettled != null && (
                  <Pill variant={n.refundSettled ? "green" : "amber"}>{n.refundSettled ? "SETTLED" : "UNSETTLED"}</Pill>
                )}
              </div>
              <div style={{ marginTop: 6 }}>
                {loading && !(n.refunds || []).length ? (
                  <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>Loading…</span>
                ) : (
                  <RefundSummary note={n} />
                )}
              </div>
            </div>

            <div>
              <Eyebrow>Line items · {items.length}</Eyebrow>
              <div style={{ border: "1px solid var(--border-default)", borderRadius: 4, overflowX: "auto", marginTop: 6 }}>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: "var(--bg-subtle)" }}>
                      <th style={panelTh}>Medicine</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 44 }}>Qty</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 64 }}>Rate</th>
                      <th style={{ ...panelTh, textAlign: "right", width: 70 }}>₹</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && items.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ ...panelTd, textAlign: "center", color: "var(--fg-muted)" }}>
                          Loading…
                        </td>
                      </tr>
                    ) : (
                      items.map((it, i) => (
                        <tr key={i}>
                          <td style={panelTd}>
                            <div style={{ fontWeight: 600, color: "var(--fg-strong)", fontSize: 11 }}>
                              {it.medicineName || it.medicineCode}
                            </div>
                            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--fg-muted)", marginTop: 2 }}>
                              {it.medicineCode} · B {it.batchNo} · Exp {it.expiryDate}
                            </div>
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)", fontWeight: 600 }}>
                            {cbFmtNum(it.quantity)}
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                            {cbFmtINR(it.sellingPrice)}
                          </td>
                          <td style={{ ...panelTd, textAlign: "right", fontFamily: "var(--font-mono)" }}>
                            {cbFmtINR(it.lineTotal)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{ padding: "8px 10px", background: "var(--bg-subtle)", borderRadius: 4, display: "flex", flexDirection: "column", gap: 4, fontSize: 12 }}>
              <div style={between}>
                <span>Taxable value</span>
                <span style={mono}>{cbFmtINR(n.taxableAmount)}</span>
              </div>
              <div style={{ ...between, color: "var(--fg-muted)" }}>
                <span>GST (CGST + SGST)</span>
                <span style={mono}>{cbFmtINR((Number(n.cgstAmount) || 0) + (Number(n.sgstAmount) || 0))}</span>
              </div>
              <div style={{ height: 1, background: "var(--border-subtle)", margin: "2px 0" }} />
              <div style={{ ...between, fontWeight: 700, color: "var(--fg-strong)" }}>
                <span>Total</span>
                <span style={mono}>{cbFmtINR(n.netAmount)}</span>
              </div>
            </div>

            {status === "DRAFT" && (
              <div style={{ padding: "10px 12px", background: "var(--ink-50)", borderRadius: 4, fontSize: 11, color: "var(--ink-700)", lineHeight: 1.5 }}>
                <strong>On posting:</strong> stock for {items.length} lot{items.length !== 1 ? "s" : ""} will be restocked (
                <Pill variant="green" style={{ fontSize: 9, height: 14, padding: "0 4px", verticalAlign: "baseline" }}>
                  RETURN_IN
                </Pill>
                ) and the refund
                {(n.refunds || []).length ? " settled as shown above" : " — none recorded, so no money settles"}.
              </div>
            )}
          </>
        )}
      </div>

      {!error && (
        <div style={{ padding: "10px 16px", borderTop: "1px solid var(--border-default)", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {status === "DRAFT" ? (
            <>
              <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconEdit size={14} />} onClick={() => onEdit(id)} disabled={busy}>
                Edit
              </Button>
              <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconX size={14} />} onClick={cancel} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="primary"
                style={{ flex: 1.4, justifyContent: "center" }}
                icon={<IconCheck size={14} />}
                onClick={post}
                disabled={busy}
              >
                {busy ? "Working…" : "Post return"}
              </Button>
            </>
          ) : (
            /* POSTED or CANCELLED — the note is final, only the ledger remains. */
            <Button style={{ flex: 1, justifyContent: "center" }} icon={<IconBook size={14} />} onClick={viewLedger}>
              View ledger
            </Button>
          )}
        </div>
      )}
    </aside>
  );
};

export default SalesReturn;
